// A page load only ever ends its session once, but expiry can be detected by several
// concurrent failing requests — so the event is broadcast at most once per page load.
//
// Only expiry ends the session in place. Sign out is a plain external navigation: the
// page unload already tears everything down, and emitting before it would leave the page
// half cleaned up if the user cancels the navigation (e.g. the uploader's beforeunload prompt).
let hasEnded = false;

// A page restored from the back/forward cache keeps its JS state, so after an expiry it
// would come back with polling stopped for good. Reload it to start from a clean session.
if (process.client) {
  window.addEventListener('pageshow', event => {
    if (event.persisted && hasEnded) window.location.reload();
  });
}

export function useSessionEnd() {
  const eventBus = useEventBus(SESSION_ENDED_EVENT);

  // Broadcasts that the session is over so listeners can clean up (stop polling,
  // timers, etc.) while the app shows the expiry notice and navigates away.
  function endSession(reason) {
    if (process.server || hasEnded) return;
    hasEnded = true;
    eventBus.emit({reason});
  }

  // For work that starts after the event already fired (e.g. a poller set up late).
  function hasSessionEnded() {
    return hasEnded;
  }

  // The event fires at most once, so handlers remove themselves after running.
  // Returns an unsubscribe function for callers that detach earlier (e.g. on unmount).
  function onSessionEnded(handler) {
    return eventBus.once(handler);
  }

  return {endSession, hasSessionEnded, onSessionEnded};
}
